import { graphql } from "gatsby";
import styled from "@emotion/styled";
import React, { useEffect } from "react";
import { isEmpty } from "lodash";

import ContentContainer from "~/components/layout/ContentContainer";
import Primary from "~/components/layout/Primary";
import TwoColumnLayout from "~/components/layout/TwoColumnLayout";
import Seo from "~/components/Seo";
import ArticleMetaData from "~/components/ArticleMetaData";

import { popHeroBlock, shiftHeroBlock } from "~/utilities/helpers";

import {
    NewsArticleBlockList,
    NewsArticleBreadcrumbs,
    NewsArticleCtaBar,
    NewsArticleNews,
    NewsArticlePageHeader,
    NewsArticleSidebar,
} from "./components";

import { useFormContext } from "~/context/FormContext";

const EmptyWrapper = styled.div``;

const Header = ({
    hidePageHeader,
    location,
    data,
    urlPermutations,
    breadcrumbsAtTop,
}) => {
    if (hidePageHeader) {
        const wpNewsArticle = data?.wpNewsArticle || {};
        const newWpNewsArticle = popHeroBlock(wpNewsArticle);

        if (isEmpty(newWpNewsArticle)) {
            return null;
        }

        const newData = { ...data, wpNewsArticle: newWpNewsArticle };

        return (
            <NewsArticleBlockList
                data={newData}
                urlPermutations={urlPermutations}
            />
        );
    }

    return (
        <>
            {breadcrumbsAtTop ? (
                <>
                    <NewsArticleBreadcrumbs data={data} />
                    <NewsArticlePageHeader
                        data={data}
                        location={location}
                        urlPermutations={urlPermutations}
                    />
                </>
            ) : (
                <>
                    <NewsArticlePageHeader
                        data={data}
                        location={location}
                        urlPermutations={urlPermutations}
                    />
                    <NewsArticleBreadcrumbs data={data} />
                </>
            )}
        </>
    );
};

const MainContent = ({ data, urlPermutations }) => {
    const wpNewsArticle = data?.wpNewsArticle || {};
    const newWpEvent = shiftHeroBlock(wpNewsArticle);
    const newData = { ...data, wpNewsArticle: newWpEvent };

    return (
        <NewsArticleBlockList
            data={newData}
            urlPermutations={urlPermutations}
        />
    );
};

const NewsArticle = ({ data, location, pageContext }) => {
    const {
        wpNewsArticle: {
            languageCode,
            articleOptions,
            title,
            date,
            articleMetadata,
        },
    } = data;

    const { setHiddenFields } = useFormContext();

    useEffect(() => {
        setHiddenFields({
            pageTitle: title,
            pageUrl: location.href,
        });
    }, [setHiddenFields, location.href, title]);

    const { hidePageHeader, hideSidebar, breadcrumbsAtTop, isFullWidth } =
        articleOptions || {};

    const urlPermutations = pageContext.urlPermutations;

    const Wrapper = isFullWidth ? EmptyWrapper : ContentContainer;

    return (
        <Primary {...{ urlPermutations, pageCode: languageCode }}>
            <Header
                hidePageHeader={hidePageHeader}
                location={location}
                data={data}
                urlPermutations={urlPermutations}
                breadcrumbsAtTop={breadcrumbsAtTop}
            />

            <ArticleMetaData
                date={date}
                title={title}
                fullWidth={hideSidebar}
                articleMetadata={articleMetadata}
            />

            <Wrapper>
                <TwoColumnLayout
                    stickySidebar
                    main={
                        <MainContent
                            data={data}
                            urlPermutations={urlPermutations}
                        />
                    }
                    sidebar={<NewsArticleSidebar data={data} />}
                    hideSidebar={hideSidebar}
                    hideSidebarOnMobile
                />
            </Wrapper>

            <NewsArticleCtaBar data={data} />

            <NewsArticleNews data={data} />
        </Primary>
    );
};

export default NewsArticle;

export const Head = ({ data, location, pageContext }) => {
    const {
        wpNewsArticle: { title, details, seo, featuredImage, language },
    } = data;

    const urlPermutations = pageContext.urlPermutations;

    return (
        <Seo
            {...{ seo, featuredImage, location, urlPermutations }}
            pageTitle={title}
            cardCopy={details.cardCopy}
            languageCode={language?.code}
        />
    );
};

export const query = graphql`
    query NewsArticleQuery($slug: String!) {
        wp {
            optionsPage {
                globalOptions {
                    hideTwitter {
                        hideNewsTwitter
                    }
                }
            }
        }
        wpNewsArticle(slug: { eq: $slug }) {
            ...NewsArticleFragment
            language {
                code
            }
            date(formatString: "MMM DD, YYYY")
            seo {
                ...SeoPostFragment
            }
            articleMetadata {
                ...ArticleMetadataFragment
            }
            articleOptions {
                hideSidebar
                hidePageHeader
                breadcrumbsAtTop
                isFullWidth
            }
            featuredImage {
                node {
                    sourceUrl
                }
            }
            details {
                cardCopy
            }
            ctaSections {
                sections {
                    title
                    links {
                        linkLabel
                        linkUrl
                        icon
                        type
                        formId
                        doc {
                            ...DocumentFragment
                        }
                    }
                }
            }
            productLines {
                nodes {
                    link: uri
                    name
                    slug
                }
            }
            blocks {
                ...AcfQuoteBlockFragment
                ...AccordionsBlockFragment
                ...AdFullBlockFragment
                ...AdWithDropdownBlockFragment
                ...AlertBlockFragment
                ...ButtonsBlockFragment
                ...CardLinksBlockFragment
                ...CodeBlockFragment
                ...ColumnsBlockFragment
                ...ContactBlockFragment
                ...ContentBlockFragment
                ...CookieTypesBlockFragment
                ...CtaSectionsBlockFragment
                ...DocumentBlockFragment
                ...DocumentLibraryBlockFragment
                ...EmbedBlockFragment
                ...EventsBlockFragment
                ...FeaturedKcLeadershipBlockFragment
                ...FeaturedNewsBlockFragment
                ...FeaturedProductsCarouselBlockFragment
                ...FeaturedResourcesBlockFragment
                ...FileBlockFragment
                ...FormBlockFragment
                ...FreeformBlockFragment
                ...FullWidthCtaBlockFragment
                ...GraphicWithHotspotsBlockFragment
                ...GroupBlockFragment
                ...GroupOfLinksBlockFragment
                ...HeadingBlockFragment
                ...HeroBlockFragment
                ...HeroSliderBlockFragment
                ...HtmlBlockFragment
                ...ImageBlockFragment
                ...ImageSliderBlockFragment
                ...InfoCardsBlockFragment
                ...IntroductionBlockFragment
                ...JsEmbedBlockFragment
                ...JumpLinksBlockFragment
                ...KnowledgeCenterBlockFragment
                ...KnowledgeCenterFilterBlockFragment
                ...LatestNewsBlockFragment
                ...LightboxImageBlockFragment
                ...LinkSectionBlockFragment
                ...ListBlockFragment
                ...LottieBlockFragment
                ...MathjaxBlockFragment
                ...MaterialsPartsBuilderBlockFragment
                ...MediaTextBlockFragment
                ...NewsBlockFragment
                ...NewsCardCarouselBlockFragment
                ...PageHeaderBlockFragment
                ...PartnersBlockFragment
                ...ParagraphBlockFragment
                ...PastEventsBlockFragment
                ...ProductTableBlockFragment
                ...ProductCardSectionBlockFragment
                ...PullquoteBlockFragment
                ...QuoteBlockFragment
                ...ReferenceDesignCardsBlockFragment
                ...ReferenceDesignsTableBlockFragment
                ...ResourcesBlockFragment
                ...SeparatorBlockFragment
                ...SidebarTabsBlockFragment
                ...SidebarTitleSectionBlockFragment
                ...SpacerBlockFragment
                ...StatCardsBlockFragment
                ...TabbedAdBlockFragment
                ...TableBlockFragment
                ...TabSectionBlockFragment
                ...TestimonialsBlockFragment
                ...ThreeColumnCardsBlockFragment
                ...TimelineBlockFragment
                ...TwitterTimelineBlockFragment
                ...UpcomingEventsBlockFragment
                ...VideoBlockFragment
                ...VideoAdBlockFragment
                ...VideoEmbedBlockFragment
                ...VideoPlaylistBlockFragment
                ...WhereToBuyBlockFragment
            }
        }
    }
`;
